import { useRef } from "react";
import {
  AddressLookup,
  Collapse,
  Box,
  Select,
  TextField,
  Text,
  type AddressDataType,
  IconFa,
} from "@cruk/cruk-react-components";
import { type UseFormReturn } from "react-hook-form";
import type * as z from "zod";
import { faMagnifyingGlassLocation } from "@fortawesome/free-solid-svg-icons";

import countries from "@fwa/src/components/AddressFields/countries.json";

import sharedStyles from "@fwa/src/components/styles.module.css";

import { type addressValidation } from "@fwa/src/validation/address";

const POSTCODE_ANYWHERE_API_KEY =
  process.env["NEXT_PUBLIC_POSTCODE_ANYWHERE_API_KEY"] || "";
const ukCountries = ["GBR", "GGY", "IMN", "JEY"];
const isUk = (countryCode: string) => ukCountries.includes(countryCode);

export type AddressFieldsValues = {
  address: z.infer<typeof addressValidation>;
};

export const AddressFields = <T extends AddressFieldsValues>(
  props: UseFormReturn<T>,
) => {
  const assertProps = props as unknown as UseFormReturn<AddressFieldsValues>;
  const { register, setValue, formState, trigger, watch } = assertProps;
  const searchRef = useRef<HTMLInputElement>(null);
  const topAddressRef = useRef<HTMLDivElement>(null);

  const watchCountry = watch("address.country");

  const handleAddressLookupChange = () => {
    setValue("address.validated", false);
  };

  const handleAddressSelected = (address: AddressDataType) => {
    setValue("address.country", address.CountryIso3);
    setValue("address.line1", address.Line1);
    setValue("address.line2", address.Line2);
    setValue("address.line3", address.Line3);
    setValue("address.city", address.City);
    setValue("address.postalCode", address.PostalCode);
    setValue("address.validated", true);
    trigger().catch(() => {
      // do nothing
    });
    if (searchRef.current) {
      searchRef.current.value = "";
    }
    if (topAddressRef.current) {
      topAddressRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <>
      <div className={sharedStyles["field-wrapper"]} data-input-width="medium">
        <Select
          {...register("address.country")}
          onChange={(e) => {
            setValue("address.validated", false);
            register("address.country")
              .onChange(e)
              .catch(() => {
                // don't throw on blur error and break from
              });
            trigger().catch(() => {
              // don't throw on request to revalidate
            });
          }}
          label="Country"
          name="address.country"
          required
          errorMessage={formState.errors?.address?.country?.message}
        >
          {countries.map(({ code, name }) => (
            <option value={code} key={code}>
              {name}
            </option>
          ))}
        </Select>
      </div>
      {isUk(watchCountry) ? (
        <div className={sharedStyles["field-wrapper"]} data-margin-bottom="m">
          <div className={sharedStyles["row"]}>
            <div style={{ width: "calc(100% - 2em)" }}>
              <AddressLookup
                name="addressSearch"
                ref={searchRef}
                required={false}
                label="Address search"
                apiKey={POSTCODE_ANYWHERE_API_KEY}
                countries={ukCountries}
                onAddressSelected={handleAddressSelected}
                onChange={handleAddressLookupChange}
              />
            </div>

            <div className={sharedStyles["column-reverse"]}>
              <Box as="span" padding="xs" marginBottom="xs">
                <IconFa
                  faIcon={faMagnifyingGlassLocation}
                  size="1.5em"
                  color="secondary"
                />
              </Box>
            </div>
          </div>
        </div>
      ) : null}
      <Box marginTop="xs" marginBottom="none">
        <Collapse
          headerTitleText="Why are we asking for this?"
          id="address-gdpr-explanation"
        >
          <Text marginBottom="m">
            We may use this information to identify if you’ve supported Cancer
            Research UK before. This will help us to ensure that our records are
            up to date so that you can receive appropriate communications from
            us.
          </Text>
        </Collapse>
      </Box>
      <div className={sharedStyles["field-wrapper"]}>
        <div ref={topAddressRef}>
          <TextField
            {...register("address.line1")}
            onChange={(e) => {
              setValue("address.validated", false);
              register("address.line1")
                .onChange(e)
                .catch(() => {
                  // don't throw on element change error out of our control
                });
            }}
            autoComplete="address-line1"
            label="Address line 1"
            errorMessage={formState.errors?.address?.line1?.message}
            required
          />
        </div>
      </div>
      <div className={sharedStyles["field-wrapper"]}>
        <TextField
          {...register("address.line2")}
          onChange={(e) => {
            setValue("address.validated", false);
            register("address.line2")
              .onChange(e)
              .catch(() => {
                // don't throw on element change error out of our control
              });
          }}
          label="Address line 2"
          errorMessage={formState.errors?.address?.line2?.message}
        />
      </div>
      <div className={sharedStyles["field-wrapper"]}>
        <TextField
          {...register("address.line3")}
          onChange={(e) => {
            setValue("address.validated", false);
            register("address.line3")
              .onChange(e)
              .catch(() => {
                // don't throw on element change error out of our control
              });
          }}
          label="Address line 3"
          errorMessage={formState.errors?.address?.line3?.message}
        />
      </div>
      <div className={sharedStyles["field-wrapper"]} data-input-width="medium">
        <TextField
          {...register("address.city")}
          onChange={(e) => {
            setValue("address.validated", false);
            register("address.city")
              .onChange(e)
              .catch(() => {
                // don't throw on element change error out of our control
              });
          }}
          label="City"
          autoComplete="address-level2"
          errorMessage={formState.errors?.address?.city?.message}
          required
        />
      </div>
      <div className={sharedStyles["field-wrapper"]} data-input-width="small">
        <TextField
          {...register("address.postalCode")}
          onChange={(e) => {
            setValue("address.validated", false);
            register("address.postalCode")
              .onChange(e)
              .catch(() => {
                // don't throw on element change error out of our control
              });
          }}
          label="Postcode"
          autoComplete="postal-code"
          errorMessage={formState.errors?.address?.postalCode?.message}
          required
        />
      </div>
    </>
  );
};

export default AddressFields;
